/**
 * The `#tag` field: chips, a fragment being typed, and a menu of what already exists.
 *
 * **The rules this obeys are not written here.** They are in
 * [se.soderbjorn.lunapin.clientserver.TagInputRules], in the one module both this and
 * the bookmarklet popup's hand-written script compile against, precisely so that the two
 * implementations cannot drift on the ones that matter. Read that first; this file is
 * how they are carried out in the browser, and nothing more.
 *
 * ── Why the rules are pure functions and the widget is thin ───────────────────
 *
 * Everything above [TagInputField] is a function of its arguments: no DOM, no state, no
 * `document`. That is what lets the rules be tested as rules — `TagInputTest` asserts the
 * ordering, the placement of *Create*, what counts as the fragment and what each key
 * does, without mounting anything. The widget below is then a driver: it reads the input,
 * calls one of these, and writes the answer back.
 *
 * It mattered more than usual here because for one ticket **nothing in `:web` consumed
 * this at all**. The edit window (LPN-10) is the first field that does; the list window's
 * search (LPN-16) is the second, and the popup is a third that cannot use this control and
 * mirrors the rules by hand instead.
 *
 * ── And why the host owns the committed set ───────────────────────────────────
 *
 * [TagInputField] is told what is committed and reports what changed; it never holds the
 * set. That is the seam the list window needs: its tag panel and its search field are two
 * views of one filter, and removing a chip in either must remove it in both. A control
 * holding the set privately would make the panel a second source of truth about the same
 * filter, and the two would disagree the first time one of them repainted.
 *
 * The same seam is what let LPN-66 put the *joins* in here without this file learning
 * anything about filters. A [CommittedTag] may carry a [Joiner] — the word drawn before it —
 * and the word, its tooltip and what pressing it does are all the host's, decided from the
 * host's own model. This draws it.
 */
package se.soderbjorn.lunapin.web

import kotlinx.browser.document
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.KeyboardEvent
import se.soderbjorn.lunapin.clientserver.TagInputRules
import se.soderbjorn.lunapin.clientserver.TagUse

// ── The rules ────────────────────────────────────────────────────────────────

/**
 * A tag the host is holding, and how.
 *
 * @property tag the tag, `#`-prefixed and lower-cased.
 * @property excluded whether this is a *not this one* — the struck-through chip. Only a
 *   search field can produce one; see [TagInputRules].
 * @property fromAi whether the model put this tag on this bookmark rather than a person —
 *   [se.soderbjorn.lunapin.clientserver.Bookmark.aiTags], which is a subset of the tags
 *   and never a list beside them. Drawn as **one small `AI` mark layered on the chip**,
 *   never a second hue: the chips already spend colour on excluded-versus-not, and an AI
 *   tag is applied and filed like any other. It is deliberately smaller than the teal `AI`
 *   pill over the summary block, so a chip is not mistaken for a summary.
 *
 *   A property of the *chip* rather than of the tag, unlike [TagUse.fromAi] in the menu
 *   below: the same tag can be yours on one bookmark and the model's on another, which is
 *   why the origin is recorded per junction row on the server.
 *
 *   Defaulted false, so the fields that have no such distinction to draw — a search field
 *   filters a collection, it does not file anything — say nothing about it.
 */
data class CommittedTag(
    val tag: String,
    val excluded: Boolean = false,
    val fromAi: Boolean = false,
    val joiner: Joiner? = null,
)

/**
 * The word drawn *before* a chip, saying how it joins the one on its left.
 *
 * **A control rather than punctuation**, which is the whole of why it exists. A row of
 * three-state chips can only ever mean `a and b and not c`: the joins are implied, so the one
 * question a filter of several tags actually raises — *do I want all of these or any of them?*
 * — has to be answered by a separate knob somewhere else on the surface, which is where
 * LunaPin's `MATCH` select used to be. Drawing the join between the two chips it joins puts
 * the answer where the question is, and a [onClick] makes it the thing you press.
 *
 * Null on every field that files rather than filters. A bookmark's tags are a set; there is no
 * join between two of them to draw and nothing a word there could mean.
 *
 * @property label what it reads — `AND`, `OR`. Uppercase and mono, so it is plainly not a tag.
 * @property title the hover text, saying what pressing it would do. Empty leaves it off.
 * @property onClick null for a join that is merely stated. An excluded tag always joins with
 *   `AND` — `not #a or not #b` is not a filter anybody builds from a list of chips — so those
 *   joins are drawn and not offered.
 */
data class Joiner(
    val label: String,
    val title: String = "",
    val onClick: (() -> Unit)? = null,
)

/**
 * The `#…` token under the caret: where it starts, where it ends, and what it says.
 *
 * @property start the index of the `#`. Where the caret is left after a commit, so the
 *   next `#tag` can be typed straight away.
 * @property end one past the token's last character.
 * @property text what follows the `#`, as typed. Empty when the `#` is the whole token,
 *   which is the moment the menu should open showing everything.
 */
data class TagFragment(
    val start: Int,
    val end: Int,
    val text: String,
)

/**
 * The `#`-token the caret is in, or null when it is not in one.
 *
 * **The single most important rule in the file**, and the reason `<input list>` plus a
 * `<datalist>` cannot be used: what the menu filters on is this token, never the field's
 * whole value. A field reading `#rust #we` with the caret at the end has a fragment of
 * `we`, and must offer `#web`.
 *
 * Null when the caret is not inside a `#`-token at all — before any `#`, or past the
 * whitespace that ended one. A word without a `#` is not a tag and never opens a menu.
 *
 * The `#` has to *begin* a word, so the fragment of `https://example.com/#top` is
 * nothing. A `#` in the middle of something is part of that something.
 */
fun tagFragmentAt(value: String, caret: Int): TagFragment? {
    val at = caret.coerceIn(0, value.length)
    // Walk back to the nearest `#`. Stop at whitespace: a token ends there, so a caret
    // past it is in the next word rather than still in this one.
    var start = at
    while (start > 0 && !value[start - 1].isWhitespace() && value[start - 1] != TagInputRules.MARKER) {
        start--
    }
    if (start == 0 || value[start - 1] != TagInputRules.MARKER) return null
    start--
    if (start > 0 && !value[start - 1].isWhitespace()) return null
    // Forward to the end of the token, which may be past the caret — somebody editing the
    // middle of `#javascript` is typing that whole tag, not a prefix of it.
    var end = at
    while (end < value.length && !value[end].isWhitespace()) end++
    return TagFragment(start = start, end = end, text = value.substring(start + 1, end))
}

/** One row of the menu. */
sealed interface TagMenuRow {
    /** The tag itself, as the row commits it. */
    val tag: String

    /**
     * A tag the account already uses, with the count the row shows.
     *
     * @property use the tag and its count. Carried whole rather than flattened so the
     *   row can also draw [TagUse.fromAi]'s `AI` mark without a second lookup.
     */
    data class Existing(val use: TagUse) : TagMenuRow {
        override val tag: String get() = use.tag
    }

    /**
     * A tag that does not exist yet. **Always last**, never first — see [TagInputRules].
     */
    data class Create(override val tag: String) : TagMenuRow
}

/**
 * The menu for [fragment], in the order it is drawn.
 *
 * Matching is substring rather than prefix, so `script` finds `#javascript` — a person
 * who half-remembers a tag remembers the middle of it as often as the start.
 *
 * @param known everything the account uses, from `GET /api/tags`. Already ordered by the
 *   server; re-sorted here anyway, because the order is a rule rather than a courtesy and
 *   a caller that hands over a list from somewhere else must get the same menu.
 * @param committed what this field is already holding. Not offered again, in either row.
 * @param offerCreate whether *Create …* may appear at all. False for the search field,
 *   which filters a collection it already holds — a tag nobody has used matches nothing,
 *   so offering to create one there offers to filter to zero results.
 */
fun tagMenu(
    fragment: String,
    known: List<TagUse>,
    committed: Set<String> = emptySet(),
    offerCreate: Boolean = true,
    limit: Int = TagInputRules.MENU_LIMIT,
): List<TagMenuRow> {
    val typed = hashPrefixedTag(fragment)
    val needle = fragment.trim().trimStart(TagInputRules.MARKER).lowercase()
    val taken = committed.map { it.lowercase() }.toSet()
    val existing = known
        .filter { it.tag.lowercase() !in taken }
        .filter { needle.isEmpty() || it.tag.lowercase().contains(needle) }
        // Count descending, then name, so the order is total: two tags used the same
        // number of times must not swap places between two keystrokes.
        .sortedWith(compareByDescending<TagUse> { it.uses }.thenBy { it.tag })
        .take(limit)
        .map { TagMenuRow.Existing(it) }
    val create = when {
        !offerCreate -> null
        typed.isEmpty() -> null
        // Nothing to create when it already exists, whether or not the cap above kept it.
        known.any { it.tag.equals(typed, ignoreCase = true) } -> null
        typed.lowercase() in taken -> null
        else -> TagMenuRow.Create(typed)
    }
    return existing + listOfNotNull(create)
}

/** What a keystroke in a tag field does. */
sealed interface TagKeyOutcome {
    /**
     * A tag joins the host's set, and the text loses the fragment that named it.
     *
     * @property tag what to commit, `#`-prefixed and lower-cased.
     * @property excluded whether it is a *not this one*.
     * @property value the field's text with the fragment removed.
     * @property caret where to put the caret: exactly where the fragment began, so the
     *   next `#tag` follows immediately.
     */
    data class Commit(
        val tag: String,
        val excluded: Boolean,
        val value: String,
        val caret: Int,
    ) : TagKeyOutcome

    /** Close the menu. **Commits nothing** — the text stays exactly as typed. */
    data object DismissMenu : TagKeyOutcome

    /**
     * Take the last chip off the strip, because `⌫` was pressed with nothing left to delete.
     *
     * The one gesture that reaches a committed tag without the mouse, and the convention
     * every chip field has: once the text is gone, the next `⌫` keeps deleting backwards
     * into the chips. It says *last* rather than naming one because the field does not hold
     * the set — the host does, and the host is handed the tag it drew last.
     *
     * **The strip's order is the strip's**, not the model's. A search field draws the
     * included tags and then the excluded ones, so the chip this takes off is the rightmost
     * one on screen, which is the one the caret is backing into.
     */
    data object RemoveLast : TagKeyOutcome

    /** Move the highlight to this row. */
    data class MoveHighlight(val to: Int) : TagKeyOutcome

    /** Not this control's key. The browser, or the surrounding form, gets it. */
    data object Ignored : TagKeyOutcome
}

/**
 * What [key] does, given what is on screen.
 *
 * Pure, and every rule [TagInputRules] states about keys is decided here — so the widget
 * below has no key logic of its own and the popup's script has one function to mirror.
 *
 * @param key the `KeyboardEvent.key` value.
 * @param altKey whether ⌥ was held. Only `⌥⏎` means anything, and only where
 *   [allowExclude] says a tag is a filter.
 * @param menu the rows currently drawn, in order. Empty means no menu is open.
 * @param highlighted the index into [menu] that is highlighted, or -1 for none.
 * @param allowExclude whether this field can exclude — the search field, and only it.
 *   Where it cannot, `⌥⏎` commits plainly rather than doing nothing: a modifier held by
 *   accident should not swallow the key.
 * @param hasCommitted whether there is a chip to take off — see [TagKeyOutcome.RemoveLast].
 *   Not defaulted, because the answer is a fact about the *shape* of the field rather than
 *   about its contents: a chip strip says whether it currently has any, and a field whose
 *   text **is** the list ([TagListField]) says false always, since there is nothing beside
 *   the text for `⌫` to reach into and ordinary deleting is all it can mean there.
 */
fun tagKeyOutcome(
    key: String,
    altKey: Boolean,
    value: String,
    caret: Int,
    menu: List<TagMenuRow>,
    highlighted: Int,
    allowExclude: Boolean,
    hasCommitted: Boolean,
): TagKeyOutcome {
    val fragment = tagFragmentAt(value, caret)
    return when (key) {
        // Escape is the promise that typing straight past the menu is always allowed. It
        // closes and nothing else — no commit, no change to the text.
        "Escape" -> if (menu.isEmpty()) TagKeyOutcome.Ignored else TagKeyOutcome.DismissMenu

        "ArrowDown" -> if (menu.isEmpty()) {
            TagKeyOutcome.Ignored
        } else {
            TagKeyOutcome.MoveHighlight((highlighted + 1).mod(menu.size))
        }

        "ArrowUp" -> if (menu.isEmpty()) {
            TagKeyOutcome.Ignored
        } else {
            TagKeyOutcome.MoveHighlight((highlighted - 1).mod(menu.size))
        }

        // ── `⌫` past the start of the text ───────────────────────────────
        //
        // **Only on an empty field**, which is what makes this safe to do without a
        // confirmation and without arming a chip first. A caret at nought with text after it
        // is somebody who moved there to edit; an empty field is somebody who has already
        // deleted everything and is still pressing the key, and the only thing left to the
        // left of the caret is the chips.
        //
        // The alternative — light the last chip on the first press and remove it on the
        // second — is what a few chip fields do, and it is machinery bought to protect a
        // gesture that is already visible and already trivially undone: the chip is a tag
        // you can press again, and in a search field the rows come straight back.
        "Backspace" -> if (value.isEmpty() && hasCommitted) {
            TagKeyOutcome.RemoveLast
        } else {
            TagKeyOutcome.Ignored
        }

        "Enter" -> {
            // Nothing is being typed as a tag, so this key is not ours — which is what
            // lets Enter in the popup's field submit the form, as somebody who has
            // finished typing expects.
            if (fragment == null) return TagKeyOutcome.Ignored
            // The highlighted row when there is one, otherwise the fragment as typed.
            // Both are commits; the menu is a shortcut to spelling, not a gate.
            val tag = menu.getOrNull(highlighted)?.tag ?: hashPrefixedTag(fragment.text)
            if (tag.isEmpty()) return TagKeyOutcome.Ignored
            TagKeyOutcome.Commit(
                tag = tag,
                excluded = altKey && allowExclude,
                value = value.removeRange(fragment.start, fragment.end),
                caret = fragment.start,
            )
        }

        else -> TagKeyOutcome.Ignored
    }
}

/**
 * The tags still loose in [text] when somebody leaves the field, in the order typed.
 *
 * The other half of [tagKeyOutcome]'s `Enter`: that one is the rule while typing, this one
 * is the rule for having stopped. See [TagInputRules] for why leaving must commit at all,
 * and [TagInputField.everythingIsATag] for the field this may not be asked about.
 *
 * **Split on commas as well as whitespace**, and a bare word counts. Somebody typing into a
 * field where every word is a label is naming tags whether or not they reached for `#`, and
 * `rust, web` is how a person who has met one of these fields before types two of them.
 *
 * @param committed what the host already holds, so leaving a field that repeats a chip does
 *   not ask for it twice. Compared lower-cased, like every other tag comparison here.
 */
fun tagsLeftInField(text: String, committed: Set<String> = emptySet()): List<String> {
    val taken = committed.map { it.lowercase() }.toMutableSet()
    return text.split(' ', '\t', '\n', ',')
        .map { hashPrefixedTag(it) }
        .filter { it.isNotEmpty() }
        // Deduplicated against the host's set *and* against itself, so `#rust #rust` is one
        // request rather than two the host has to make idempotent on its own.
        .filter { taken.add(it.lowercase()) }
}

/**
 * One tag, trimmed and guaranteed to start with exactly one `#`, lower-cased.
 *
 * A small local copy of the server's `hashPrefixed`, and worth saying why that is not a
 * duplicated rule: **the server is the authority and re-normalises everything it is
 * sent.** This exists so the chip a person sees the instant they press `⏎` reads the way
 * the stored tag will, rather than flickering when the save comes back. If the two ever
 * disagree, the server's answer is the one the collection has.
 */
internal fun hashPrefixedTag(tag: String): String {
    val bare = tag.trim().trimStart(TagInputRules.MARKER).trim().lowercase()
    return if (bare.isEmpty()) "" else "${TagInputRules.MARKER}$bare"
}

// ── The widget ───────────────────────────────────────────────────────────────

/**
 * A field that takes `#tag`s: chips for what is committed, text for what is being typed,
 * and a menu under the caret.
 *
 * **The host owns the committed set.** Construct one, hand it the account's tags with
 * [setKnownTags] and the current set with [setCommitted], and listen to [onCommit] and
 * [onRemove]. It never decides what is committed; it says what somebody asked for and
 * repaints whatever it is told afterwards. See the file header for why.
 *
 * @param placeholder what the empty field says.
 * @param allowExclude whether `⌥⏎` may produce a struck-through *not this one*. The
 *   search field only; see [TagInputRules].
 * @param offerCreate whether *Create …* may appear. The fields that file a bookmark
 *   only.
 * @param everythingIsATag whether every word in this field is a label, which decides
 *   **whether leaving the field commits what is still typed in it**. True for the fields
 *   that file — the edit window and the two settings lists — where text left behind has no
 *   meaning at all and dropping it silently loses a tag somebody typed. False for the list
 *   window's search field, the one field whose leftover text is a real thing: a search over
 *   title, URL and summary. See [TagInputRules], which states the rule for both
 *   implementations.
 *
 *   Defaulted true because the fields that file are the common case and the search field is
 *   the single exception; a new field that forgets to think about this gets the behaviour
 *   that loses nothing.
 * @param onCommit somebody committed a tag. The host adds it to its set and calls
 *   [setCommitted] — or does not, which is a legitimate answer and the reason this is a
 *   request rather than a notification.
 * @param onRemove somebody pressed a chip's `×`.
 * @param onTextChanged the free text now in the field, which is everything that is not a
 *   committed tag. The search field searches title, URL and summary with it.
 */
class TagInputField(
    placeholder: String = "",
    private val allowExclude: Boolean = false,
    private val offerCreate: Boolean = true,
    private val everythingIsATag: Boolean = true,
    private val onCommit: (CommittedTag) -> Unit = {},
    private val onRemove: (CommittedTag) -> Unit = {},
    private val onTextChanged: (String) -> Unit = {},
) {
    /** What to mount. A `div` holding the chips, the input and the menu. */
    val root: HTMLElement = element("div", "lp-tagfield")

    private val chips: HTMLElement = element("div", "lp-tagfield-chips")
    private val input: HTMLInputElement = (document.createElement("input") as HTMLInputElement).apply {
        type = "text"
        className = "lp-tagfield-input"
        this.placeholder = placeholder
        // A hand-written combobox, for the reason `TagInputRules` gives: a `<datalist>`
        // matches the input's whole value and stops matching after the first tag.
        setAttribute("role", "combobox")
        setAttribute("aria-autocomplete", "list")
        setAttribute("aria-expanded", "false")
        autocomplete = "off"
    }
    /** The menu, which this field draws but does not decide. See [TagMenuPopover]. */
    private val popover = TagMenuPopover(input) { index -> commitRow(index) }

    private var known: List<TagUse> = emptyList()
    private var committed: List<CommittedTag> = emptyList()

    init {
        root.appendChild(chips)
        val field = element("div", "lp-tagfield-box")
        field.appendChild(input)
        field.appendChild(popover.element)
        root.appendChild(field)

        input.addEventListener("input", { refreshMenu(); onTextChanged(input.value) })
        input.addEventListener("focus", { refreshMenu() })
        // A frame's grace before closing, or a click on a row would land after the menu
        // had already gone. `mousedown` on the rows preempts the blur for the mouse; this
        // is for everything else that moves focus.
        //
        // Leaving also commits what is still typed, where the field is all tags — see
        // [commitLeftovers]. Ordered before the close deliberately: it reads the text, and
        // closing the menu does not touch the text, but a future close that did would make
        // this a silent no-op rather than a visible bug.
        input.addEventListener("blur", { commitLeftovers(); closeMenu() })
        input.addEventListener("keydown", { event -> onKeyDown(event as KeyboardEvent) })
    }

    /**
     * The account's tags, from `GET /api/tags`. Safe to call again when they change.
     *
     * **A no-op when they have not.** Hosts hand this over on every paint, and a list window
     * paints on every keystroke — so without the guard, typing `#kot` rebuilt the menu once
     * for the `input` event and again for each of the two calls the repaint makes, three
     * times a character, each one sorting the whole vocabulary and wiping the popover's DOM.
     * The vocabulary itself changes only when a fetch answers.
     */
    fun setKnownTags(tags: List<TagUse>) {
        if (known == tags) return
        known = tags
        if (popover.isOpen) refreshMenu()
    }

    /** What the host is holding. The control draws this and never edits it. See [setKnownTags]. */
    fun setCommitted(tags: List<CommittedTag>) {
        if (committed == tags) return
        committed = tags
        renderChips()
        if (popover.isOpen) refreshMenu()
    }

    /** The free text — everything typed that is not a committed tag. */
    fun text(): String = input.value

    /**
     * Put [value] in the field, for a host restoring text it stored.
     *
     * The one write into this control's own input, and it exists for exactly one case: the
     * list window's search text lives in the workspace, so a window reopened after a reload
     * has to show what it was searching for in a field nobody has touched yet. It does
     * **not** exist for keeping the field in step with the host — the free text flows
     * outwards through `onTextChanged`, and a host that wrote it back on every state would
     * be fighting the person typing.
     *
     * Which is why the caller is expected to check that the field is not focused first;
     * [textInput] is published for that and for nothing else.
     */
    fun setText(value: String) {
        if (input.value != value) input.value = value
    }

    /**
     * The `<input>` itself, so a host can ask whether the caret is in it.
     *
     * Published reluctantly and for that question alone. Everything else about this control
     * is reached through the functions above; reaching for the element to *change* it would
     * put the field's rules in two places, which is the thing the file header is about.
     */
    val textInput: HTMLInputElement get() = input

    /** Put the caret in the field. */
    fun focus() {
        input.focus()
    }

    private fun renderChips() {
        chips.innerHTML = ""
        committed.forEach { tag ->
            // Before the chip, never after it, so a strip that wraps breaks *between* a
            // joiner and the chip it belongs to rather than orphaning the word at the end of
            // a line. See [Joiner] for why this is a button at all.
            tag.joiner?.let { joiner ->
                val press = joiner.onClick
                val word = if (press == null) {
                    element("span", "lp-tagfield-joiner", joiner.label)
                } else {
                    button(joiner.label, "lp-tagfield-joiner lp-tagfield-joiner-live") { press() }
                }
                if (joiner.title.isNotEmpty()) word.title = joiner.title
                chips.appendChild(word)
            }
            val chip = element(
                "span",
                if (tag.excluded) "lp-tagfield-chip lp-tagfield-chip-excluded" else "lp-tagfield-chip",
            )
            chip.appendChild(element("span", "lp-tagfield-chip-label", tag.tag))
            // One small mark, and only where the host said so — see [CommittedTag.fromAi].
            if (tag.fromAi) chip.appendChild(element("span", "lp-tagfield-chip-ai", "AI"))
            chip.appendChild(button("×", "lp-tagfield-chip-remove") { onRemove(tag) })
            chips.appendChild(chip)
        }
    }

    /** Rebuild the menu from what is under the caret, or close it if nothing is. */
    private fun refreshMenu() {
        val fragment = tagFragmentAt(input.value, input.selectionStart ?: input.value.length)
        if (fragment == null) {
            closeMenu()
            return
        }
        popover.open(
            tagMenu(
                fragment = fragment.text,
                known = known,
                committed = committed.map { it.tag }.toSet(),
                offerCreate = offerCreate,
            ),
        )
    }

    private fun closeMenu() = popover.close()

    /**
     * Fold whatever is still typed into chips, because the person has moved on.
     *
     * The fix for the inconsistency somebody hits within a minute of meeting this field:
     * `⏎` turns what you typed into a chip, and Tab or a click elsewhere used to turn it
     * into nothing at all. In the settings lists that quietly dropped a tag; in the edit
     * window, typing `#foo` and reaching straight for **Save** — the most natural order to
     * do it in — saved the bookmark without it.
     *
     * **As typed, never the highlighted row.** `applyEnter` takes the highlight because
     * pressing `⏎` on an open menu is somebody engaging with the offer; leaving is not, and
     * a field that filed `#web` because the menu happened to be highlighting it while you
     * typed `#we` would be inventing a decision. The menu is a shortcut to spelling, and a
     * shortcut nobody took is not taken.
     *
     * Reported through [onCommit] one tag at a time, like every other commit, so the host
     * sees no new shape — and it is [onCommit] rather than anything local because this
     * control still does not own the set.
     */
    private fun commitLeftovers() {
        if (!everythingIsATag) return
        val left = tagsLeftInField(input.value, committed.map { it.tag }.toSet())
        // The text goes whether or not anything came out of it: a field left holding `#`
        // or a stray comma has nothing to commit and nothing worth keeping either.
        if (input.value.isEmpty() && left.isEmpty()) return
        input.value = ""
        onTextChanged("")
        left.forEach { onCommit(CommittedTag(tag = it)) }
    }

    /** Commit the row at [index] as if `⏎` had been pressed on it. */
    private fun commitRow(index: Int) {
        popover.moveHighlight(index)
        applyEnter(altKey = false)
    }

    private fun onKeyDown(event: KeyboardEvent) {
        val outcome = tagKeyOutcome(
            key = event.key,
            altKey = event.altKey,
            value = input.value,
            caret = input.selectionStart ?: input.value.length,
            menu = popover.rows,
            highlighted = popover.highlighted,
            allowExclude = allowExclude,
            hasCommitted = committed.isNotEmpty(),
        )
        when (outcome) {
            // Ignored is the important case and the one that must not be swallowed: it is
            // what lets Enter reach a surrounding form and Escape reach a dialog.
            TagKeyOutcome.Ignored -> return

            TagKeyOutcome.DismissMenu -> {
                event.consume()
                closeMenu()
            }

            // Reported outwards like every other removal, and **not** applied here: this
            // control draws the set and does not own it, so a chip taken off by the keyboard
            // has to travel the same path as one taken off by its `×`. The host answers with
            // a new [setCommitted], which is what redraws the strip.
            TagKeyOutcome.RemoveLast -> {
                event.consume()
                committed.lastOrNull()?.let { onRemove(it) }
            }

            is TagKeyOutcome.MoveHighlight -> {
                event.consume()
                popover.moveHighlight(outcome.to)
            }

            is TagKeyOutcome.Commit -> {
                event.consume()
                apply(outcome)
            }
        }
    }

    /** Re-run the Enter rule against the current state, for a row that was clicked. */
    private fun applyEnter(altKey: Boolean) {
        val outcome = tagKeyOutcome(
            key = "Enter",
            altKey = altKey,
            value = input.value,
            caret = input.selectionStart ?: input.value.length,
            menu = popover.rows,
            highlighted = popover.highlighted,
            allowExclude = allowExclude,
            // Immaterial here — this asks the `Enter` rule and nothing else — but stated
            // truthfully rather than as a convenient false.
            hasCommitted = committed.isNotEmpty(),
        )
        if (outcome is TagKeyOutcome.Commit) apply(outcome)
    }

    /**
     * This key was for the menu, and nothing else may act on it.
     *
     * **Both halves, and `stopPropagation` is the one that matters.** `preventDefault`
     * alone only marks the event handled — every listener above still runs, and the surface
     * this control is mounted in cannot tell "the menu answered this Escape" from "the
     * toolkit marked this Escape" by looking at `defaultPrevented`, because the shell marks
     * it too. The edit window found that out the direct way: with only `preventDefault`
     * here, dismissing the menu also closed the window under it.
     *
     * Applied to exactly the three outcomes that *are* this control's — a commit, a
     * highlight move, a dismissal — and never to [TagKeyOutcome.Ignored], which is the case
     * that keeps Enter reaching a form and Escape reaching a dialog.
     */
    private fun KeyboardEvent.consume() {
        preventDefault()
        stopPropagation()
    }

    private fun apply(commit: TagKeyOutcome.Commit) {
        input.value = commit.value
        // The caret goes exactly where the fragment began, which is the whole point of
        // committing to a chip: the next `#` is typed without moving anything.
        input.selectionStart = commit.caret
        input.selectionEnd = commit.caret
        closeMenu()
        onTextChanged(input.value)
        onCommit(CommittedTag(tag = commit.tag, excluded = commit.excluded))
    }
}
